import React from 'react';
import { Calendar, MapPin, Briefcase, GraduationCap, Code, Users, Target, Zap } from 'lucide-react';
import { experience, personalInfo } from '../../mock/data';
import { getYearsSince, yearsWord } from '../../lib/dateUtils';
import './Experience.css';

// Total de tecnologias distintas somando todas as experiências.
const totalTechnologies = new Set(
  experience.flatMap(exp => exp.technologies)
).size;

const Experience = ({ language }) => {
  const experienceContent = {
    pt: {
      title: 'Experiência',
      subtitle: 'Minha jornada profissional e acadêmica no desenvolvimento de software',
      current: 'Atual',
      achievements: 'Conquistas',
      technologies: 'Tecnologias',
      workType: {
        work: 'Profissional',
        education: 'Acadêmica'
      }
    },
    en: {
      title: 'Experience',
      subtitle: 'My professional and academic journey in software development',
      current: 'Current',
      achievements: 'Achievements',
      technologies: 'Technologies',
      workType: {
        work: 'Professional',
        education: 'Academic'
      }
    }
  };

  const content = experienceContent[language];

  const yearsOfExperience = getYearsSince(personalInfo.startDate);

  return (
    <section id="experience" className="experience">
      <div className="container">
        <div className="experience-header">
          <h2 className="section-title">{content.title}</h2>
          <p className="section-subtitle">{content.subtitle}</p>
        </div>

        <div className="experience-timeline">
          {experience.map((exp, index) => (
            <div key={exp.id} className={`timeline-item ${exp.type}`}>
              <div className="timeline-marker">
                <div className="marker-icon">
                  {exp.type === 'work' ? (
                    <Briefcase size={20} />
                  ) : (
                    <GraduationCap size={20} />
                  )}
                </div>
                <div className="marker-line"></div>
              </div>

              <div className="timeline-content">
                <div className="experience-card">
                  <div className="card-header">
                    <div className="header-main">
                      <div className="company-logo">
                        {exp.type === 'work' ? (
                          <Briefcase size={24} />
                        ) : (
                          <GraduationCap size={24} />
                        )}
                      </div>
                      <div className="header-info">
                        <h3 className="position">{exp.position[language]}</h3>
                        <h4 className="company">{exp.company}</h4>
                        <div className="meta-info">
                          <div className="duration">
                            <Calendar size={14} />
                            <span>{exp.duration[language]}</span>
                          </div>
                          <div className="location">
                            <MapPin size={14} />
                            <span>{exp.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="type-badge">
                      <span className={`badge ${exp.type}`}>
                        {content.workType[exp.type]}
                      </span>
                      {exp.current && (
                        <span className="current-badge">{content.current}</span>
                      )}
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="achievements-section">
                      <h5 className="section-subtitle-small">
                        <Target size={16} />
                        {content.achievements}
                      </h5>
                      <ul className="achievements-list">
                        {exp.description[language].map((achievement, achieveIndex) => (
                          <li key={achieveIndex} className="achievement-item">
                            <div className="achievement-bullet">
                              <Zap size={12} />
                            </div>
                            <span className="achievement-text">{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="technologies-section">
                      <h5 className="section-subtitle-small">
                        <Code size={16} />
                        {content.technologies}
                      </h5>
                      <div className="tech-tags">
                        {exp.technologies.map((tech, techIndex) => (
                          <span key={techIndex} className="tech-tag">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="experience-summary">
          <div className="summary-card">
            <div className="summary-icon">
              <Users size={32} />
            </div>
            <div className="summary-content">
              <h3 className="summary-title">
                {language === 'pt' 
                  ? 'Crescimento Contínuo' 
                  : 'Continuous Growth'
                }
              </h3>
              <p className="summary-description">
                {language === 'pt'
                  ? 'Combinando experiência acadêmica sólida com aplicação prática no mercado, estou sempre buscando novos desafios e oportunidades de aprendizado.'
                  : 'Combining solid academic experience with practical market application, I\'m always seeking new challenges and learning opportunities.'
                }
              </p>
              <div className="summary-stats">
                <div className="stat">
                  <div className="stat-number">{yearsOfExperience}+</div>
                  <div className="stat-label">
                    {language === 'pt'
                      ? `${yearsWord(yearsOfExperience, 'pt')} de Experiência`
                      : `${yearsWord(yearsOfExperience, 'en')} of Experience`
                    }
                  </div>
                </div>
                <div className="stat">
                  <div className="stat-number">{experience.length}</div>
                  <div className="stat-label">
                    {language === 'pt' ? 'Organizações' : 'Organizations'}
                  </div>
                </div>
                <div className="stat">
                  <div className="stat-number">{totalTechnologies}</div>
                  <div className="stat-label">
                    {language === 'pt' ? 'Tecnologias' : 'Technologies'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;